import { useEffect, useMemo, useState } from 'react';

import { buildCategoryMegaMenuItems } from '@/utilities/buildCategoryMegaMenuItems';
import { normalizeCategoryTreePayload } from '@/utilities/normalizeCategoryTreePayload';

import { getCategoryTreeService } from '@/client/services/core/category/methods';
import { CategoryTreeNode } from '@/server/services/core/category/models';

export const useCategoryMegaMenuData = (initialCategories: CategoryTreeNode[] = []) => {
    const [categories, setCategories] = useState<CategoryTreeNode[]>(initialCategories);
    const [isCategoryMegaMenuLoaded, setIsCategoryMegaMenuLoaded] = useState(initialCategories.length > 0);

    useEffect(() => {
        if (!initialCategories.length) {
            return;
        }

        setCategories(initialCategories);
        setIsCategoryMegaMenuLoaded(true);
    }, [initialCategories]);

    useEffect(() => {
        if (isCategoryMegaMenuLoaded) {
            return;
        }

        let active = true;

        const fetchCategoryTree = async () => {
            try {
                const response = await getCategoryTreeService();

                if (!active) {
                    return;
                }

                setCategories(normalizeCategoryTreePayload(response?.data));
            } catch {
                if (active) {
                    setCategories([]);
                }
            } finally {
                if (active) {
                    setIsCategoryMegaMenuLoaded(true);
                }
            }
        };

        void fetchCategoryTree();

        return () => {
            active = false;
        };
    }, [isCategoryMegaMenuLoaded]);

    const categoryMegaMenuItems = useMemo(() => buildCategoryMegaMenuItems(categories), [categories]);

    return { categoryMegaMenuItems, isCategoryMegaMenuLoaded };
};
